import React, { useMemo } from 'react';
import ContainerSectionTitle from 'components/content/shared/other/ContainerSectionTitle';
import { Column } from 'react-table';
import SimpleDataTable from 'components/content/shared/tables/SimpleDataTable';
import { ITablePropertyExtensions } from 'types/components/react_table/IReactTablePropertyExtensions';
import { formatToEuro } from 'functions/formatters/currency';
import NoDataMessageContainer from 'components/content/shared/other/NoDataMessageContainer';
import { cnTblFootRow, cnTblFootCell, cnTblFootCellDense } from 'components/content/shared/tables/TableStyleConstants';
import { nameofFactory } from 'functions/misc/NameOf';
import { toPercentageString } from 'functions/formatters/numbers';
import { IInvoiceLine } from 'types/backend_models/invoices/IInvoiceLine';
import { tooltipUoM, tooltipQty } from 'components/content/shared/Tooltips';

interface IProps
{
	invoiceLines: IInvoiceLine[]
}

const containerTitle = 'Invoice lines';

export function InvoiceDetailLinesContainer(props: IProps)
{
	const columns = useMemo(() =>
	{
		return createColumns();
	}, []);
	const totalPrice = useMemo(() => getTotalPrice(props.invoiceLines), [props]);

	if (props.invoiceLines.length === 0) {
		return (
			<div>
				<ContainerSectionTitle>{containerTitle}</ContainerSectionTitle>
				<div className="h-200">
					<NoDataMessageContainer containerClassName="h100p" message="Invoice has no invoicelines." />
				</div>
			</div>
		)
	}

	return (
		<div>
			<ContainerSectionTitle>{containerTitle}</ContainerSectionTitle>
			<div style={{ overflowX: 'auto' }}>
				<SimpleDataTable
					columns={columns}
					data={props.invoiceLines}
					propertyExtensions={TablepropsExtensions}
					onRowClick={()=>null}
					footer={
						<tr className={cnTblFootRow} style={{ textAlign: 'right' }}>
							<td className={`${cnTblFootCell} ${cnTblFootCellDense}`} colSpan={columns.length}>
								<span className="mgr-35" style={{ fontWeight: 500 }}>Invoice total</span>{formatToEuro(totalPrice)}
							</td>
						</tr>
					}
				/>
			</div>
		</div>
	);
};

function getTotalPrice(invoiceLines: IInvoiceLine[]): number
{
	let total = 0;
	invoiceLines.forEach(il => total += il.price);
	return total;
}

const invoiceNameOf = nameofFactory<IInvoiceLine>();

function createColumns(): Column<Object>[]
{
	// Column definitions for orders table
	const columns: Column<Object>[] = [

		{
			Header: 'Line id',
			accessor: invoiceNameOf('id') as keyof Object,
			width: 30
		},
		// {
		// 	Header: "KEY REF",
		// 	accessor: invoiceNameOf("keyRef") as keyof Object,
		// 	maxWidth: undefined,
		// 	width: 75
		// },
		{
			Header: "Customer REF",
			accessor: invoiceNameOf("productRef") as keyof Object,
			maxWidth: undefined,
			width: 75
		},
		{
			Header: "Customer Description",
			accessor: invoiceNameOf('productDescription') as keyof Object,
			maxWidth: undefined,
		},
		{
			Header: (props) => tooltipQty(),
			accessor: invoiceNameOf('qty') as keyof Object,
			maxWidth: undefined,
			width: 25
		},
		{
			Header: (props) => tooltipUoM(),
			accessor: invoiceNameOf('unitOfMeasure') as keyof Object,
			maxWidth: undefined,
			width: 25
		},
		{
			Header: 'Unit price',
			accessor: invoiceNameOf('unitPrice') as keyof Object,
			maxWidth: undefined,
			width: 25,
			Cell: (props: { cell: { value: any; }; }) => formatToEuro(Number(props.cell.value))
		},
		{
			Header: 'Discount',
			accessor: invoiceNameOf('discount') as keyof Object,
			maxWidth: undefined,
			width: 25,
			Cell: (props: { cell: { value: number; }; }) => toPercentageString(props.cell.value as number)
		},
		{
			Header: 'Total Price',
			accessor: invoiceNameOf('price') as keyof Object,
			maxWidth: undefined,
			width: 30,
			Cell: (props: { cell: { value: any; }; }) => formatToEuro(Number(props.cell.value))
		}
	];

	return columns;
}

/** Used to apply additional column styling */
const TablepropsExtensions: ITablePropertyExtensions = {
	columnExtensions: [
		{ accessor: invoiceNameOf('qty'), style: { textAlign: 'right' } },
		{ accessor: invoiceNameOf('unitOfMeasure'), style: { textAlign: 'right' } },
		{ accessor: invoiceNameOf('unitPrice'), style: { textAlign: 'right' } },
		{ accessor: invoiceNameOf('discount'), style: { textAlign: 'right' } },
		{ accessor: invoiceNameOf('price'), style: { textAlign: 'right' } }
	]
}